import type { LocalizedString } from '@/services/dto';
import { AppUtils } from '@/utils/app';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    categoryOnFloor?: LocalizedString;
    tabIndex?: number;
    selectedFloorName?: LocalizedString;
    isShowAllFloorGuide?: boolean;
};

export const HorizontalMapHeader = ({ categoryOnFloor, tabIndex, selectedFloorName, isShowAllFloorGuide = false }: Props) => {
    const { t, i18n } = useTranslation();

    const categoryDisplayText = useMemo(() => {
        if (!categoryOnFloor) {
            return '';
        }
        return categoryOnFloor[i18n.language] ?? '';
    }, [categoryOnFloor, i18n.language]);

    const floorDisplayText = useMemo(() => {
        if (isShowAllFloorGuide) {
            return t('floor_guide.label.all_floors_guide');
        }
        return selectedFloorName?.[i18n.language] ?? selectedFloorName?.ko ?? '';
    }, [selectedFloorName, i18n.language, isShowAllFloorGuide, t]);

    const floorAriaLabel = useMemo(() => {
        if (isShowAllFloorGuide) {
            return t('floor_guide.label.all_floors_guide');
        }
        const floorName = selectedFloorName?.[i18n.language] ?? selectedFloorName?.ko ?? '';
        return t('floor_guide.text_to_speech.floor', {
            floorName: AppUtils.getFloorTTSText(floorName),
        });
    }, [selectedFloorName, i18n.language, isShowAllFloorGuide, t]);

    return (
        <section
            className="flex flex-row items-center gap-[calc(20*var(--vw-unit))] py-[calc(17*var(--vh-unit))] pl-[calc(46*var(--vw-unit))]"
            data-region-nav
        >
            {floorDisplayText && (
                <p
                    tabIndex={tabIndex}
                    data-interactive
                    data-region-order={1}
                    aria-label={floorAriaLabel}
                    className="text-text-main01 focus-border font-pretendard flex shrink-0 items-center text-center text-[calc(80*var(--vw-unit))] leading-[1] font-bold tracking-[calc(-1.6*var(--vw-unit))]"
                >
                    {floorDisplayText}
                </p>
            )}
            {categoryDisplayText && (
                <p
                    tabIndex={tabIndex ? tabIndex + 1 : undefined}
                    data-interactive
                    data-region-order={2}
                    aria-label={categoryDisplayText}
                    className="text-text-main01 focus-border text-center text-[calc(26*var(--vw-unit))] leading-[calc(40*var(--vh-unit))] font-semibold tracking-[calc(-0.52*var(--vw-unit))]"
                >
                    {categoryDisplayText || <span>&nbsp;</span>}
                </p>
            )}
        </section>
    );
};
